import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import useIsMobile from 'app/hooks/useIsMobile';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import { type ContactFormLinkLocation } from 'app/modules/contact-form/stores/ContactFormStore';
import { useContactFormState } from 'app/modules/contact-form/stores/useContactFormState';
import { getConfig } from 'app/services/config';
import { ModuleItemActionType } from 'app/services/helpCenter';
import { getContactFormSlugFromUrl, isContactFormURL } from 'app/utils/contactFormUrl';
import getUtmParams from 'app/utils/getUtmParams';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useInterstitialPage from './useInterstitialPage';
import usePdfViewerEnabled from './usePdfViewerEnabled';
import { usePersistedLocation } from './usePersistedLocation';
import usePersistedSearch from './usePersistedSearch';
import { useSearch } from './useSearch';
import useShouldAutoOpen from './useShouldAutoOpen';

export function isInternalLink(url: string): boolean {
  if (!url || url.startsWith('#')) {
    return false;
  }

  if (url.startsWith('/')) {
    return true;
  }

  const urlObj = new URL(url);
  return urlObj.hostname === window.location.hostname;
}

export function isContactURL(url: string): boolean {
  return /^(tel|mailto):/gim.test(url);
}

export function checkIsPdf(url: string) {
  const urlObj = new URL(url, window.location.origin);
  return urlObj.pathname.includes('.pdf');
}

function isContactFormAction(action?: string) {
  return action === ModuleItemActionType.OPEN_CONTACT_FORM;
}

export function shouldShowInterstitial(url: string, interstitialEnabled: boolean): boolean {
  const urlParams = new URLSearchParams(new URL(url, window.location.origin).search);
  return !isContactURL(url) && !urlParams.has('bypassInterstitial') && !isInternalLink(url) && interstitialEnabled;
}

function useLinks() {
  const { data: helpCenter } = useFetchHelpCenter();
  const isMobile = useIsMobile();
  const shouldAutoOpen = !isMobile;
  const location = useLocation();
  const navigate = useNavigate();
  const [, setShouldAutoOpen] = useShouldAutoOpen();
  const [, setPersistedSearch] = usePersistedSearch();
  const [, setPersistedLocation] = usePersistedLocation();
  const { search } = useSearch();
  const urlConfig = helpCenter?.external_url_param_config;
  const pdfViewerEnabled = usePdfViewerEnabled();
  const { openContactForm } = useContactFormState();
  const { isInterstitialEnabledForURL } = useInterstitialPage();
  const { setIsOpen } = useTailorIsOpen();
  const featureFlags = useFeatureFlags();

  interface LinkDetails {
    type?: 'resource' | 'external';
    action_type?: ModuleItemActionType;
    source?: 'widget' | 'nonWidget';
    linkLocation?: ContactFormLinkLocation;
  }

  const shouldOpenInNewTab = useCallback(
    (url: string, linkDetails?: LinkDetails): boolean => {
      const isPdf = checkIsPdf(url);
      const isResource = linkDetails?.type === 'resource';
      const isExternal = !isInternalLink(url);

      return isMobile || isPdf || isResource || isExternal;
    },
    [isMobile]
  );

  const addExternalParam = useCallback(
    (url: URL) => {
      if (urlConfig?.enabled) {
        url.searchParams.set(urlConfig.name, urlConfig.value);
      }

      return url;
    },
    [urlConfig]
  );

  const openLink = useCallback(
    (url: string, linkDetails?: LinkDetails) => {
      if (isMobile || isInternalLink(url)) {
        setPersistedSearch(search);
        setPersistedLocation(location.pathname);
      }

      if (isContactFormURL(url) || isContactFormAction(linkDetails?.action_type)) {
        setIsOpen(true);
        return openContactForm(
          getContactFormSlugFromUrl(url),
          linkDetails?.source || 'widget',
          linkDetails?.linkLocation
        );
      }

      setShouldAutoOpen(shouldAutoOpen);

      const interstitialEnabled = isInterstitialEnabledForURL(url);
      let urlObject = new URL(url);

      if (!checkIsPdf(url)) {
        urlObject = addExternalParam(new URL(url));
      }

      if (!isInternalLink(url) && helpCenter?.customization?.fe_config) {
        const utmParams = getUtmParams(helpCenter.customization.fe_config);
        utmParams.forEach((value, key) => {
          urlObject.searchParams.set(key, value);
        });
      }

      if (shouldShowInterstitial(url, interstitialEnabled)) {
        return navigate('/tailor/interstitial', {
          state: { destination: urlObject.toString() },
        });
      }

      if (isContactURL(url)) {
        return window.open(url, '_self');
      }

      urlObject.searchParams.delete('bypassInterstitial');

      if (checkIsPdf(url) && pdfViewerEnabled) {
        const pdfHostname = getConfig('APP_URL');
        const pdfHtmlFile = featureFlags.uiRefreshAdobePdfViewer ? 'pdf2.html' : 'pdf.html';

        const pdfUrl = `${pdfHostname}/${pdfHtmlFile}?url=${urlObject}`;
        return window.open(pdfUrl, shouldOpenInNewTab(url, linkDetails) ? '_blank' : '_self');
      }

      return window.open(urlObject.toString(), shouldOpenInNewTab(url, linkDetails) ? '_blank' : '_self');
    },
    [
      isMobile,
      shouldAutoOpen,
      search,
      location.pathname,
      setPersistedSearch,
      setPersistedLocation,
      setShouldAutoOpen,
      navigate,
      shouldOpenInNewTab,
      pdfViewerEnabled,
      openContactForm,
      addExternalParam,
      isInterstitialEnabledForURL,
      setIsOpen,
      featureFlags.uiRefreshAdobePdfViewer,
      helpCenter?.customization?.fe_config,
    ]
  );

  const getLinkTarget = useCallback(
    (url: string): string => {
      return shouldOpenInNewTab(url) ? '_blank' : '_self';
    },
    [shouldOpenInNewTab]
  );

  const showInterstitialForURL = useCallback(
    (url: string) => {
      const enabledForSpecificURL = isInterstitialEnabledForURL(url);
      const enabledForGeneralURL = !!helpCenter?.interstitial_page?.display_interstitial;

      return enabledForSpecificURL || shouldShowInterstitial(url, enabledForGeneralURL);
    },
    [isInterstitialEnabledForURL, helpCenter]
  );

  return {
    openLink,
    getLinkTarget,
    checkIsPdf,
    shouldShowInterstitial: showInterstitialForURL,
  };
}

export default useLinks;
